import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Home, Search, Pencil, Trash2 } from 'lucide-react';
import AppLayout from '../components/AppLayout';
import Drawer from '../components/Drawer';
import SubdivisionFormSlider from '../components/SubdivisionFormSlider';
import { subdivisionService } from '../services/subdivisionService';
import { Subdivision } from '../types/subdivision';
import '../styles/Projects.css';
import '../styles/Buttons.css';
import '../styles/DataTable.css';

interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

const Subdivisions: React.FC = () => {
  const navigate = useNavigate();
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [subdivisions, setSubdivisions] = useState<Subdivision[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [subdivisionToDelete, setSubdivisionToDelete] = useState<Subdivision | null>(null);

  // Get drawer state from URL params
  const isDrawerOpen = searchParams.get('action') === 'new' || !!searchParams.get('edit');
  const editSubdivisionId = searchParams.get('edit');

  useEffect(() => {
    if (workspaceId) {
      fetchSubdivisions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, pagination.page, searchQuery]);

  const fetchSubdivisions = async () => {
    if (!workspaceId) return;

    try {
      setLoading(true);
      setError('');

      const response = await subdivisionService.getAll(workspaceId, {
        page: pagination.page,
        limit: pagination.limit,
        search: searchQuery
      });

      setSubdivisions(response.subdivisions);
      setPagination(response.pagination);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch subdivisions');
      console.error('Error fetching subdivisions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToWorkspaces = () => {
    navigate('/workspaces');
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPagination({ ...pagination, page: 1 });
  };

  const handlePageChange = (page: number) => {
    setPagination({ ...pagination, page });
  };

  const handleCreateSubdivision = () => {
    setSearchParams({ action: 'new' });
  };

  const handleEditClick = (e: React.MouseEvent, subdivisionId: string) => {
    e.stopPropagation();
    setSearchParams({ edit: subdivisionId });
  };

  const handleCloseDrawer = () => {
    setSearchParams({});
    fetchSubdivisions();
  };

  const handleDeleteClick = (e: React.MouseEvent, subdivision: Subdivision) => {
    e.stopPropagation();
    setSubdivisionToDelete(subdivision);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!subdivisionToDelete || !workspaceId) return;

    try {
      await subdivisionService.delete(workspaceId, subdivisionToDelete._id);
      setShowDeleteModal(false);
      setSubdivisionToDelete(null);
      fetchSubdivisions();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete subdivision');
      console.error('Error deleting subdivision:', err);
    }
  };

  if (!workspaceId) {
    return (
      <AppLayout onBack={handleBackToWorkspaces} backLabel="Workspaces">
        <div className="projects-container">
          <div className="form-error">
            <p>No workspace selected</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout onBack={handleBackToWorkspaces} backLabel="Workspaces">
      <div className="projects-container">
        {/* Section Header */}
        <div className="projects-section-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2 className="projects-section-title">Subdivisions</h2>
              <p className="projects-section-subtitle">
                Manage subdivision information and associations
              </p>
            </div>
            <button className="btn btn-primary btn-md" onClick={handleCreateSubdivision}>
              + New Subdivision
            </button>
          </div>
        </div>

        {/* Card Container for Search and Table */}
        <div style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 2px 8px rgba(72, 24, 47, 0.08)'
        }}>
          {/* Actions Bar */}
          <div className="projects-actions">
            <div className="projects-search">
              <input
                type="text"
                placeholder="Search subdivisions..."
                value={searchQuery}
                onChange={handleSearchChange}
              />
            </div>
          </div>

          {/* Error State */}
          {error && (
            <div className="form-error" style={{ marginBottom: '24px' }}>
              {error}
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="projects-loading">
              <div className="projects-loading-spinner" />
            </div>
          )}

          {/* Empty State */}
          {!loading && subdivisions.length === 0 && !searchQuery && (
            <div className="projects-empty">
              <div className="projects-empty-icon" style={{ color: '#6B7280' }}>
                <Home size={48} />
              </div>
              <h3 className="projects-empty-title">No subdivisions yet</h3>
              <p className="projects-empty-text">
                Get started by adding your first subdivision
              </p>
              <button className="btn btn-primary btn-lg" onClick={handleCreateSubdivision}>
                Create your first subdivision
              </button>
            </div>
          )}

          {/* No Search Results */}
          {!loading && subdivisions.length === 0 && searchQuery && (
            <div className="projects-empty">
              <div className="projects-empty-icon" style={{ color: '#6B7280' }}>
                <Search size={48} />
              </div>
              <h3 className="projects-empty-title">No subdivisions found</h3>
              <p className="projects-empty-text">
                Try adjusting your search criteria
              </p>
            </div>
          )}

          {/* Subdivisions Table */}
          {!loading && subdivisions.length > 0 && (
          <>
            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Subdivision Name</th>
                    <th>Builder</th>
                    <th>Location</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {subdivisions.map((subdivision) => (
                    <tr
                      key={subdivision._id}
                    >
                      <td className="data-table-primary-cell">{subdivision.name}</td>
                      <td>{subdivision.builderName || '—'}</td>
                      <td>{subdivision.location || '—'}</td>
                      <td>{new Date(subdivision.createdAt).toLocaleDateString()}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            className="btn-icon"
                            onClick={(e) => handleEditClick(e, subdivision._id)}
                            title="Edit"
                            style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center', color: '#48182F' }}
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            className="btn-icon"
                            onClick={(e) => handleDeleteClick(e, subdivision)}
                            title="Delete"
                            style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center', color: '#DC2626' }}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="projects-pagination">
                <button
                  className="projects-pagination-btn"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                >
                  ← Prev
                </button>
                {Array.from({ length: pagination.pages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    className={`projects-pagination-page ${
                      page === pagination.page ? 'active' : ''
                    }`}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                ))}
                <button
                  className="projects-pagination-btn"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                >
                  Next →
                </button>
              </div>
            )}
          </>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && subdivisionToDelete && (
        <div
          className="projects-modal-overlay"
          onClick={() => setShowDeleteModal(false)}
        >
          <div className="projects-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="projects-modal-title">Delete Subdivision</h2>
            <p className="projects-modal-subtitle">
              Are you sure you want to delete "{subdivisionToDelete.name}"? This action cannot
              be undone.
            </p>
            <div className="projects-modal-actions">
              <button
                className="btn btn-secondary btn-md"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary btn-md"
                onClick={handleDeleteConfirm}
                style={{ background: '#DC2626' }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Subdivision Form Drawer */}
      <Drawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        title={editSubdivisionId ? 'Edit Subdivision' : 'Create New Subdivision'}
        footer={
          <div className="drawer-footer-actions">
            <button
              type="button"
              className="btn btn-secondary btn-md"
              onClick={handleCloseDrawer}
              form="subdivision-form"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-md"
              form="subdivision-form"
            >
              {editSubdivisionId ? 'Update Subdivision' : 'Create Subdivision'}
            </button>
          </div>
        }
      >
        {isDrawerOpen && (
          <SubdivisionFormSlider
            workspaceId={workspaceId}
            subdivisionId={editSubdivisionId || undefined}
            onSuccess={handleCloseDrawer}
            onCancel={handleCloseDrawer}
          />
        )}
      </Drawer>
    </AppLayout>
  );
};

export default Subdivisions;
